<script setup lang="ts">
import { useDocumentVisibility, useElementVisibility, useMediaQuery } from '@vueuse/core'
import { Building2, Check, Globe2, Info, Monitor, Server, TriangleAlert, Waypoints, Zap } from 'lucide-vue-next'

type Scenario = 'normal' | 'international' | 'local' | 'destination'
type Segment = 'local' | 'crossborder' | 'egress'

const { t, locale } = useI18n()
const titleId = useId()
const root = ref<HTMLElement>()
const visible = useElementVisibility(root)
const documentVisibility = useDocumentVisibility()
const reducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)')
const scenario = ref<Scenario>('international')
const running = computed(() => visible.value && documentVisibility.value === 'visible' && !reducedMotion.value)
const scenarioOrder: Scenario[] = ['normal', 'international', 'local', 'destination']

// Relative fill and packet timing illustrate bottlenecks; they are not measured rates.
const speeds: Record<Scenario, { public: number; private: number }> = {
  normal: { public: 0.82, private: 0.82 },
  international: { public: 0.24, private: 0.82 },
  local: { public: 0.22, private: 0.22 },
  destination: { public: 0.32, private: 0.32 },
}

const paths = [
  {
    id: 'public' as const,
    nodes: [
      { label: 'device', icon: Monitor, owner: 'customer' },
      { label: 'isp', icon: Building2, owner: 'carrier' },
      { label: 'server', icon: Server, owner: 'managed' },
      { label: 'destination', icon: Globe2, owner: 'service' },
    ],
    segments: [
      { id: 'local' as const, label: 'local_access', owner: 'carrier' },
      { id: 'crossborder' as const, label: 'public_crossborder', owner: 'carrier' },
      { id: 'egress' as const, label: 'egress', owner: 'carrier' },
    ],
  },
  {
    id: 'private' as const,
    nodes: [
      { label: 'device', icon: Monitor, owner: 'customer' },
      { label: 'mainland_entry', icon: Building2, owner: 'managed' },
      { label: 'overseas_exit', icon: Server, owner: 'managed' },
      { label: 'destination', icon: Globe2, owner: 'service' },
    ],
    segments: [
      { id: 'local' as const, label: 'mainland_access', owner: 'carrier' },
      { id: 'crossborder' as const, label: 'private_crossborder', owner: 'managed' },
      { id: 'egress' as const, label: 'egress', owner: 'carrier' },
    ],
  },
]

function isLimited(route: 'public' | 'private', segment: Segment) {
  return (
    (scenario.value === 'international' && route === 'public' && segment === 'crossborder') ||
    (scenario.value === 'local' && segment === 'local') ||
    (scenario.value === 'destination' && segment === 'egress')
  )
}

const label = (key: string) => t(`dedicated_servers.bandwidth_demo.${key}`)
</script>

<template>
  <section ref="root" class="bandwidth-demo" :class="{ 'is-running': running, 'is-reduced': reducedMotion }" :data-scenario="scenario" :aria-labelledby="titleId" :lang="locale">
    <div class="speed-heading">
      <div>
        <p class="speed-eyebrow"><Waypoints aria-hidden="true" />{{ label('eyebrow') }}</p>
        <h2 :id="titleId">{{ label('title') }}</h2>
        <p class="speed-intro">{{ label('intro') }}</p>
      </div>
    </div>

    <div class="speed-scenarios" role="group" :aria-label="label('choose_scenario')">
      <button v-for="(item, index) in scenarioOrder" :key="item" type="button" class="speed-scenario" :aria-pressed="scenario === item" @click="scenario = item">
        <span class="speed-step" aria-hidden="true">{{ index + 1 }}</span>
        {{ label(`scenarios.${item}.title`) }}
      </button>
    </div>
    <p class="speed-explanation">{{ label(`scenarios.${scenario}.description`) }}</p>

    <div class="speed-routes">
      <article v-for="path in paths" :key="path.id" class="speed-route" :data-route="path.id">
        <div class="speed-route-heading">
          <h3>{{ label(`routes.${path.id}`) }}</h3>
          <span class="speed-route-status" :class="{ 'is-limited': path.segments.some((segment) => isLimited(path.id, segment.id)) }">
            {{ label(`scenarios.${scenario}.${path.id}`) }}
          </span>
        </div>
        <div class="speed-route-body">
          <div class="speed-map">
            <ol class="speed-nodes">
              <li v-for="node in path.nodes" :key="node.label" class="speed-node" :data-owner="node.owner">
                <span class="speed-node-icon"><component :is="node.icon" aria-hidden="true" /></span>
                <span class="speed-node-label">{{ label(`nodes.${node.label}`) }}</span>
                <span class="speed-node-owner">{{ label(`owners.${node.owner}`) }}</span>
              </li>
            </ol>
            <ol class="speed-segments">
              <li
                v-for="segment in path.segments"
                :key="segment.id"
                class="speed-segment"
                :class="{ 'is-limited': isLimited(path.id, segment.id), 'is-private': path.id === 'private' && segment.id === 'crossborder' }"
                :data-segment="segment.id"
                :data-owner="segment.owner"
              >
                <div class="speed-segment-status">
                  <TriangleAlert v-if="isLimited(path.id, segment.id)" :aria-label="label('bottleneck')" />
                  <Zap v-else-if="path.id === 'private' && segment.id === 'crossborder'" :aria-label="label('segments.private_crossborder')" />
                  <Check v-else :aria-label="label('normal_speed')" />
                </div>
                <div class="speed-pipe" aria-hidden="true">
                  <i
                    v-for="packet in 3"
                    :key="packet"
                    class="speed-packet"
                    :style="{ animationDuration: isLimited(path.id, segment.id) ? '4.2s' : '1.8s', animationDelay: `${packet * -0.6}s` }"
                  />
                </div>
                <span class="speed-segment-label">
                  {{ label(`segments.${segment.label}`) }}
                </span>
                <span class="speed-segment-owner">{{ label(`owners.${segment.owner}`) }}</span>
              </li>
            </ol>
          </div>
          <div class="speed-meter" role="img" :aria-label="`${label('relative_speed')}: ${label(`scenarios.${scenario}.${path.id}`)}`">
            <span>{{ label('relative_speed') }}</span>
            <div class="speed-track" aria-hidden="true">
              <i
                class="speed-fill"
                :class="{ 'is-limited': path.segments.some((segment) => isLimited(path.id, segment.id)) }"
                :style="{ transform: `scaleX(${speeds[scenario][path.id]})` }"
              />
            </div>
          </div>
        </div>
        <p v-if="path.id === 'private'" class="speed-ingress-note">
          <Info aria-hidden="true" />
          <span>{{ label('ingress_warning') }}</span>
        </p>
      </article>
    </div>

    <div class="speed-controls">
      <div class="speed-control" data-control="managed">
        <h4>{{ label('managed_title') }}</h4>
        <p>{{ label(`scenarios.${scenario}.managed`) }}</p>
      </div>
      <div class="speed-control" data-control="external">
        <h4>{{ label('external_title') }}</h4>
        <p>{{ label(`scenarios.${scenario}.external`) }}</p>
      </div>
    </div>

    <div class="speed-footer">
      <Info aria-hidden="true" />
      <div>
        <p>{{ label('takeaway') }}</p>
        <p class="speed-disclaimer">{{ label('disclaimer') }}</p>
      </div>
    </div>
  </section>
</template>

<style scoped>
.bandwidth-demo {
  --speed-ink: var(--ds-ink, var(--foreground));
  --speed-copy: var(--ds-copy, var(--muted-foreground));
  --speed-line: var(--ds-line, var(--border));
  --speed-success: #15803d;
  --speed-warning: #946100;
  margin: 3.5rem 0 0;
  padding: clamp(1.25rem, 3vw, 2rem);
  border: 1px solid var(--speed-line);
  border-radius: 1rem;
  background: color-mix(in oklch, var(--primary) 3%, var(--background));
  color: var(--speed-ink);
}
.dark .bandwidth-demo {
  --speed-success: #4ade80;
  --speed-warning: #e9b85e;
}
.bandwidth-demo :where(h2, h3, h4, p, span) {
  color: inherit;
}
.speed-heading {
  display: flex;
  align-items: start;
  justify-content: space-between;
  gap: 1.25rem;
}
.speed-eyebrow {
  display: flex;
  align-items: center;
  gap: 0.45rem;
  margin-bottom: 0.75rem;
  color: var(--speed-copy);
  font-size: 0.75rem;
  letter-spacing: 0.08em;
}
.speed-eyebrow svg {
  width: 0.9rem;
  height: 0.9rem;
  color: var(--primary);
}
.speed-heading h2 {
  font-size: clamp(1.5rem, 2.6vw, 2.1rem);
  font-weight: 600;
  line-height: 1.35;
  letter-spacing: -0.035em;
  text-wrap: balance;
}
.speed-intro {
  max-width: 45rem;
  margin-top: 0.7rem;
  color: var(--speed-copy);
  font-size: 0.85rem;
  line-height: 1.8;
}
.speed-scenario:focus-visible {
  outline: 2px solid var(--primary);
  outline-offset: 3px;
}
.speed-scenarios {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 1.6rem;
}
.speed-scenario {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  min-height: 2.6rem;
  padding: 0.5rem 0.7rem;
  border: 1px solid var(--speed-line);
  border-radius: 0.5rem;
  color: var(--speed-copy);
  font-size: 0.8rem;
  text-align: left;
  transition:
    border-color 250ms var(--ds-ease),
    background-color 250ms var(--ds-ease),
    color 250ms var(--ds-ease);
}
.speed-scenario:active {
  transform: scale(0.98);
}
.speed-scenario[aria-pressed='true'] {
  border-color: color-mix(in oklch, var(--primary) 50%, var(--speed-line));
  background: color-mix(in oklch, var(--primary) 8%, var(--background));
  color: var(--speed-ink);
}
.speed-step {
  color: var(--primary);
  font-size: 0.7rem;
  font-weight: 600;
}
.speed-explanation {
  min-height: 3.2em;
  margin: 1rem 0 0;
  font-size: 0.85rem;
  line-height: 1.6;
}
.speed-route {
  padding: 1.4rem 0;
  border-top: 1px solid var(--speed-line);
}
.speed-route-heading {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 0.75rem;
  margin-bottom: 1.2rem;
}
.speed-route-heading h3 {
  font-size: 0.95rem;
  font-weight: 600;
}
.speed-route-status {
  color: var(--primary);
  font-size: 0.75rem;
  text-align: right;
}
.speed-route-status.is-limited {
  color: var(--speed-warning);
}
.speed-route-body {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 10rem;
  align-items: center;
  gap: 2rem;
}
.speed-nodes {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  margin: 0;
  padding: 0;
  list-style: none;
}
.speed-node {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.55rem;
  text-align: center;
}
.speed-node-icon {
  display: grid;
  place-items: center;
  width: 2.1rem;
  height: 2.1rem;
  border: 1px solid var(--speed-line);
  border-radius: 0.6rem;
  background: var(--background);
  color: var(--speed-copy);
}
.speed-node-icon svg {
  width: 1rem;
  height: 1rem;
}
.speed-node-label {
  min-height: 2.8em;
  padding-inline: 0.2rem;
  font-size: 0.75rem;
  line-height: 1.4;
}
.speed-node-owner,
.speed-segment-owner {
  color: var(--speed-copy);
  font-size: 0.7rem;
  line-height: 1.4;
  text-align: center;
}
.speed-node-owner {
  min-height: 2.8em;
  padding-inline: 0.2rem;
}
.speed-node[data-owner='managed'] .speed-node-icon {
  border-color: color-mix(in oklch, var(--primary) 45%, var(--speed-line));
  color: var(--primary);
}
[data-owner='managed'] > .speed-node-owner,
[data-owner='managed'] > .speed-segment-owner {
  color: var(--primary);
}
.speed-segment-owner {
  display: block;
  min-height: 2.8em;
  margin-top: 0.25rem;
  padding-inline: 0.25rem;
}
.speed-segments {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  margin: 0 12.5%;
  padding: 0;
  list-style: none;
}
.speed-segment {
  --packet-color: var(--speed-copy);
}
.speed-segment.is-private {
  --packet-color: var(--primary);
}
.speed-segment.is-limited {
  --packet-color: var(--speed-warning);
}
.speed-segment-status {
  display: grid;
  place-items: center;
  height: 1.4rem;
  margin-bottom: 0.15rem;
  color: var(--speed-success);
}
.speed-segment-status svg {
  width: 1rem;
  height: 1rem;
}
.is-private .speed-segment-status {
  color: var(--primary);
}
.is-limited .speed-segment-status {
  color: var(--speed-warning);
}
.speed-pipe {
  position: relative;
  height: 0.8rem;
  overflow: hidden;
}
.speed-pipe::before {
  content: '';
  position: absolute;
  inset: calc(50% - 1px) 0 auto;
  height: 2px;
  background: color-mix(in oklch, var(--packet-color) 30%, transparent);
}
.speed-packet {
  position: absolute;
  inset: 0;
  width: 100%;
  animation: speed-packet-flow 1.8s linear infinite;
  animation-play-state: paused;
}
.speed-packet::after {
  content: '';
  position: absolute;
  top: calc(50% - 2px);
  left: 0;
  width: 0.7rem;
  height: 4px;
  border-radius: 1rem;
  background: var(--packet-color);
}
.is-running .speed-packet {
  animation-play-state: running;
}
.speed-segment-label {
  display: flex;
  align-items: start;
  justify-content: center;
  gap: 0.25rem;
  margin-top: 0.35rem;
  padding-inline: 0.25rem;
  color: var(--speed-copy);
  font-size: 0.7rem;
  line-height: 1.5;
  text-align: center;
}
.is-limited .speed-segment-label {
  color: var(--speed-warning);
}
.is-private .speed-segment-label {
  color: var(--primary);
}
.speed-meter {
  display: grid;
  gap: 0.6rem;
  color: var(--speed-copy);
  font-size: 0.75rem;
}
.speed-track {
  height: 0.65rem;
  overflow: hidden;
  border-radius: 1rem;
  background: color-mix(in oklch, var(--speed-copy) 12%, transparent);
}
.speed-fill {
  display: block;
  width: 100%;
  height: 100%;
  border-radius: inherit;
  background: var(--primary);
  transform-origin: left;
}
.is-running .speed-fill {
  transition: transform 200ms var(--ds-ease);
}
.speed-fill.is-limited {
  background: var(--speed-warning);
}
.speed-controls {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1.5rem;
  padding: 1.25rem 0;
  border-top: 1px solid var(--speed-line);
}
.speed-ingress-note {
  display: flex;
  align-items: start;
  gap: 0.55rem;
  margin-top: 1rem;
  color: var(--speed-copy);
  font-size: 0.8rem;
  line-height: 1.7;
}
.speed-ingress-note > svg {
  flex-shrink: 0;
  width: 0.9rem;
  height: 0.9rem;
  margin-top: 0.2rem;
}
.speed-control h4 {
  margin: 0 0 0.5rem;
  font-size: 0.8rem;
  font-weight: 600;
}
.speed-control[data-control='managed'] h4 {
  color: var(--primary);
}
.speed-control p {
  color: var(--speed-copy);
  font-size: 0.8rem;
  line-height: 1.7;
}
.speed-footer {
  display: flex;
  align-items: start;
  gap: 0.65rem;
  padding-top: 1.2rem;
  border-top: 1px solid var(--speed-line);
  font-size: 0.8rem;
  line-height: 1.7;
}
.speed-footer > svg {
  flex-shrink: 0;
  width: 1rem;
  height: 1rem;
  margin-top: 0.2rem;
  color: var(--speed-copy);
}
.speed-disclaimer {
  margin-top: 0.35rem;
  color: var(--speed-copy);
  font-size: 0.7rem;
}
@keyframes speed-packet-flow {
  0% {
    transform: translateX(-10%);
    opacity: 0;
  }
  10%,
  85% {
    opacity: 1;
  }
  100% {
    transform: translateX(100%);
    opacity: 0;
  }
}
@media (hover: hover) and (pointer: fine) {
  .speed-scenario:hover {
    border-color: var(--primary);
    color: var(--speed-ink);
  }
}
@media (max-width: 640px) {
  .speed-scenarios {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  .speed-scenario {
    font-size: 0.72rem;
  }
  .speed-explanation {
    min-height: 4.8em;
  }
  .bandwidth-demo:lang(en) .speed-explanation {
    min-height: 6.4em;
  }
  .speed-route {
    padding-block: 1rem;
  }
  .speed-route-heading {
    align-items: start;
    flex-direction: column;
    gap: 0.35rem;
  }
  .speed-route-status {
    text-align: left;
  }
  .speed-route-body {
    grid-template-columns: minmax(0, 1fr);
    gap: 0.75rem;
  }
  .speed-node-icon {
    display: none;
  }
  .speed-node-label {
    min-height: 2.8em;
    font-size: 0.7rem;
  }
  .speed-segment-label {
    min-height: 3em;
    font-size: 0.66rem;
  }
  .speed-meter {
    gap: 0.45rem;
  }
  .speed-controls {
    grid-template-columns: minmax(0, 1fr);
    gap: 1rem;
  }
}
@media (prefers-reduced-motion: reduce) {
  .speed-packet {
    animation: none;
    transform: translateX(40%);
    opacity: 0.6;
  }
  .speed-packet:not(:first-child) {
    display: none;
  }
  .speed-fill {
    transition: none;
  }
}
</style>
